import { createColumnHelper } from '@tanstack/react-table';
import {
  CheckCircle,
  Clock,
  Download,
  Edit,
  FileSpreadsheet,
  FileText,
  Menu,
  MoreHorizontal,
  Plus,
  XCircle,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import { EnrollioTable } from '~/shared/components/enrollio-table';
import { Badge } from '~/shared/shadcn/ui/badge';
import { Button } from '~/shared/shadcn/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/shared/shadcn/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/shared/shadcn/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '~/shared/shadcn/ui/sheet';

import { downloadVolunteerReport } from '../api/export-volunteer-report';
import {
  useDeleteVolunteer,
  useUpdateVolunteer,
  useVolunteers,
} from '../api/hooks/useVolunteers';
import type { RecitalVolunteer as _RecitalVolunteer } from '../model/types/volunteers';
import { VolunteerSignupForm } from './VolunteerSignupForm';

const columnHelper = createColumnHelper<_RecitalVolunteer>();

interface VolunteerManagementTableProps {
  recitalId: string;
  shows?: Array<{ _id: string; name: string }>;
}

const statusConfig = {
  confirmed: {
    label: 'Confirmed',
    variant: 'default' as const,
    icon: CheckCircle,
    color: 'text-green-600',
  },
  pending: {
    label: 'Pending',
    variant: 'secondary' as const,
    icon: Clock,
    color: 'text-yellow-600',
  },
  cancelled: {
    label: 'Cancelled',
    variant: 'destructive' as const,
    icon: XCircle,
    color: 'text-red-600',
  },
};

export function VolunteerManagementTable({
  recitalId,
  shows = [],
}: VolunteerManagementTableProps) {
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'pending' | 'confirmed' | 'cancelled'
  >('all');
  const [showFilter, setShowFilter] = useState<string>('all');
  const [addVolunteerDialogOpen, setAddVolunteerDialogOpen] = useState(false);
  const [volunteerActionsOpen, setVolunteerActionsOpen] = useState(false);
  const [editingVolunteer, setEditingVolunteer] =
    useState<_RecitalVolunteer | null>(null);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (statusFilter !== 'all') count++;
    if (showFilter !== 'all') count++;
    return count;
  }, [statusFilter, showFilter]);

  const queryParams =
    statusFilter !== 'all' ? { status: statusFilter } : undefined;

  const { data: volunteers = [], isLoading } = useVolunteers(
    recitalId,
    queryParams,
  );
  const updateVolunteer = useUpdateVolunteer();
  const deleteVolunteer = useDeleteVolunteer();

  const filteredVolunteers = volunteers.filter((volunteer) => {
    if (showFilter === 'all') return true;
    if (showFilter === 'none') return !volunteer.showId;
    // Handle both string showId and populated showId object
    const volunteerShowId =
      typeof volunteer.showId === 'object'
        ? volunteer.showId._id
        : volunteer.showId;
    return volunteerShowId === showFilter;
  });

  const handleStatusChange = async (
    volunteerId: string,
    status: 'confirmed' | 'pending' | 'cancelled',
  ) => {
    try {
      await updateVolunteer.mutateAsync({
        volunteerId,
        data: { status },
      });
    } catch (error) {
      console.error('Failed to update volunteer status:', error);
    }
  };

  const handleDelete = async (volunteerId: string) => {
    if (!confirm('Are you sure you want to delete this volunteer signup?')) {
      return;
    }
    try {
      await deleteVolunteer.mutateAsync(volunteerId);
    } catch (error) {
      console.error('Failed to delete volunteer:', error);
    }
  };

  const handleExportReport = async (format: 'csv' | 'pdf') => {
    try {
      await downloadVolunteerReport(recitalId, format);
      toast.success(`Volunteer report exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Failed to export volunteer report:', error);
      toast.error('Failed to export volunteer report');
    }
  };

  const { hasAdminAccess } = useCurrentUserRole();
  const columns = [
    columnHelper.accessor(
      (volunteer) => volunteer.name ?? volunteer.parentName ?? 'Unknown',
      {
        id: 'volunteer',
        header: 'Volunteer',
        cell: ({ row }) => (
          <div className='capitalize'>
            <div>
              {row.original.name ?? row.original.parentName ?? 'Unknown'}
            </div>
            {row.original.parentName &&
            row.original.name !== row.original.parentName ? (
              <div className='text-xs text-muted-foreground'>
                Parent: {row.original.parentName}
              </div>
            ) : null}
          </div>
        ),
      },
    ),
    columnHelper.accessor('email', {
      header: 'Email',
      cell: (info) => info.getValue() ?? 'N/A',
    }),
    columnHelper.accessor('relation', {
      header: 'Relation',
      cell: (info) => (
        <span className='capitalize'>{info.getValue() ?? 'N/A'}</span>
      ),
    }),
    columnHelper.accessor('role', { header: 'Role' }),
    columnHelper.display({
      id: 'show',
      header: 'Show',
      cell: ({ row }) =>
        row.original.showName ??
        (typeof row.original.showId === 'object'
          ? row.original.showId.name
          : undefined) ??
        'Any show',
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: ({ row }) => {
        const statusInfo = statusConfig[row.original.status];
        const StatusIcon = statusInfo.icon;
        return (
          <Badge variant={statusInfo.variant} className='gap-1'>
            <StatusIcon className='size-3' />
            {statusInfo.label}
          </Badge>
        );
      },
    }),
    columnHelper.accessor('notes', {
      header: 'Notes',
      cell: (info) => info.getValue() ?? '—',
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant='ghost' size='icon'>
              <MoreHorizontal className='size-4' />
              <span className='sr-only'>Volunteer actions</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            {row.original.status !== 'confirmed' ? (
              <DropdownMenuItem
                onClick={() =>
                  handleStatusChange(row.original._id, 'confirmed')
                }
              >
                <CheckCircle className='mr-2 size-4' />
                Confirm
              </DropdownMenuItem>
            ) : null}
            {row.original.status !== 'pending' ? (
              <DropdownMenuItem
                onClick={() => handleStatusChange(row.original._id, 'pending')}
              >
                <Clock className='mr-2 size-4' />
                Mark Pending
              </DropdownMenuItem>
            ) : null}
            {row.original.status !== 'cancelled' ? (
              <DropdownMenuItem
                onClick={() =>
                  handleStatusChange(row.original._id, 'cancelled')
                }
              >
                <XCircle className='mr-2 size-4' />
                Cancel
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem
              onClick={() => {
                setEditingVolunteer(row.original);
              }}
            >
              <Edit className='mr-2 size-4' />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              className='text-destructive'
              onClick={() => handleDelete(row.original._id)}
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    }),
  ];

  const renderVolunteerFilters = (stacked: boolean) => (
    <>
      <div className={stacked ? 'space-y-2' : undefined}>
        {stacked ? (
          <label className='block text-sm font-medium'>Status</label>
        ) : null}
        <Select
          value={statusFilter}
          onValueChange={(value) => {
            setStatusFilter(
              value as 'all' | 'pending' | 'confirmed' | 'cancelled',
            );
          }}
        >
          <SelectTrigger className={stacked ? 'w-full' : 'w-[180px]'}>
            <SelectValue placeholder='Filter by status' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All Statuses</SelectItem>
            <SelectItem value='pending'>Pending</SelectItem>
            <SelectItem value='confirmed'>Confirmed</SelectItem>
            <SelectItem value='cancelled'>Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {shows.length > 0 ? (
        <div className={stacked ? 'space-y-2' : undefined}>
          {stacked ? (
            <label className='block text-sm font-medium'>Show</label>
          ) : null}
          <Select value={showFilter} onValueChange={setShowFilter}>
            <SelectTrigger className={stacked ? 'w-full' : 'w-[200px]'}>
              <SelectValue placeholder='Filter by show' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All Shows</SelectItem>
              <SelectItem value='none'>General (No Show)</SelectItem>
              {shows.map((show) => (
                <SelectItem key={show._id} value={show._id}>
                  {show.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}
    </>
  );

  if (isLoading) {
    return <div className='p-4 text-center'>Loading volunteers...</div>;
  }

  return (
    <div className='space-y-4'>
      <EnrollioTable
        totalLabel='Volunteers'
        filtersLayout='inline'
        filterHeader={
          <>
            <Sheet
              open={volunteerActionsOpen}
              onOpenChange={setVolunteerActionsOpen}
            >
              <SheetTrigger asChild>
                <Button variant='outline' className='relative w-full md:hidden'>
                  <Menu className='mr-2 size-4' />
                  Volunteer actions
                  {activeFilterCount > 0 ? (
                    <Badge className='ml-2' variant='secondary'>
                      {activeFilterCount}
                    </Badge>
                  ) : null}
                </Button>
              </SheetTrigger>
              <SheetContent side='right' className='w-[300px] overflow-y-auto'>
                <SheetHeader>
                  <SheetTitle>Volunteer actions</SheetTitle>
                  <SheetDescription>
                    Add volunteers, export the list, or change filters.
                  </SheetDescription>
                </SheetHeader>
                <div className='mt-6 flex flex-col gap-3'>
                  {hasAdminAccess ? (
                    <Button
                      className='w-full justify-start'
                      size='sm'
                      onClick={() => {
                        setVolunteerActionsOpen(false);
                        setAddVolunteerDialogOpen(true);
                      }}
                    >
                      <Plus className='mr-2 size-4' />
                      Add Volunteer
                    </Button>
                  ) : null}
                  <Button
                    variant='outline'
                    className='w-full justify-start'
                    size='sm'
                    onClick={() => {
                      setVolunteerActionsOpen(false);
                      void handleExportReport('csv');
                    }}
                  >
                    <FileSpreadsheet className='mr-2 size-4' />
                    Export as CSV
                  </Button>
                  <Button
                    variant='outline'
                    className='w-full justify-start'
                    size='sm'
                    onClick={() => {
                      setVolunteerActionsOpen(false);
                      void handleExportReport('pdf');
                    }}
                  >
                    <FileText className='mr-2 size-4' />
                    Export as PDF
                  </Button>
                  <div className='border-t pt-3' />
                  {renderVolunteerFilters(true)}
                </div>
              </SheetContent>
            </Sheet>
            <div className='hidden flex-wrap items-center gap-2 md:flex'>
              {renderVolunteerFilters(false)}
            </div>
          </>
        }
        toolbar={
          <div className='hidden flex-wrap gap-2 md:flex'>
            {hasAdminAccess ? (
              <Button
                onClick={() => {
                  setAddVolunteerDialogOpen(true);
                }}
                size='sm'
              >
                <Plus />
                Add Volunteer
              </Button>
            ) : null}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant='outline' size='sm'>
                  <Download />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuItem
                  onClick={() => {
                    void handleExportReport('csv');
                  }}
                >
                  <FileSpreadsheet />
                  Export as CSV
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    void handleExportReport('pdf');
                  }}
                >
                  <FileText />
                  Export as PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
        data={filteredVolunteers}
        columns={columns}
        getRowId={(row) => row._id}
        emptyContent='No volunteers found.'
        ariaLabel='Recital volunteers'
      />

      <VolunteerSignupForm
        recitalId={recitalId}
        shows={shows}
        open={addVolunteerDialogOpen}
        onOpenChange={setAddVolunteerDialogOpen}
        onSuccess={() => {
          toast.success('Volunteer added successfully!');
        }}
      />

      <VolunteerSignupForm
        recitalId={recitalId}
        shows={shows}
        open={!!editingVolunteer}
        onOpenChange={(open) => {
          if (!open) setEditingVolunteer(null);
        }}
        volunteer={editingVolunteer}
        onSuccess={() => {
          toast.success('Volunteer updated successfully!');
          setEditingVolunteer(null);
        }}
      />
    </div>
  );
}
